import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Home, MapPin, Upload,
    Search, AlertCircle,
    Loader2, X
} from 'lucide-react';
import HouseService from '../../services/HouseService';
import { uploadErrorMessage } from '../../utils/uploadErrors';
import { MAX_IMAGE_UPLOAD_MB } from '../../constants/uploads';
import { toast, FormSection, InputGroup, Input, Select, DateField, PageHeader } from '../../components/v2/V2Components';
import usePageView from '../../hooks/usePageView';
import { useFilePicker } from '../../hooks/useFilePicker';
import analytics from '../../services/AnalyticsService';
import { ANALYTICS_EVENTS } from '../../constants/analyticsEvents';

// Configuration options are now fetched dynamically from the backend

const hasAutofillValue = (value) => value !== null && value !== undefined && value !== '';

const mergeAutofillData = (currentData, incomingData = {}) => {
    const nextData = { ...currentData };

    Object.entries(incomingData).forEach(([key, value]) => {
        const shouldMerge = (key in currentData) && hasAutofillValue(value) && !hasAutofillValue(currentData[key]);
        if (shouldMerge) nextData[key] = value ?? '';
    });

    return nextData;
};

const withImportedOption = (options, currentValue) => {
    if (!currentValue || options.some((option) => option.value === currentValue)) {
        return options;
    }

    return [...options, { value: currentValue, label: currentValue }];
};

// One message for banner + toast — they used to disagree ("Please correct
// the errors." vs "Validation failed").
const VALIDATION_MESSAGE = 'Please fix the highlighted fields.';

// Backend validation errors are keyed address_<field> / house_<field>
// (api/houses.py create/update WTForms handling), while form inputs use
// bare names. Map between them so per-field wiring and clear-on-edit
// use the same key space.
const ADDRESS_FIELDS = new Set(['street', 'unit', 'city', 'state', 'zip_code', 'country', 'county']);
const fieldErrorKey = (name) => (ADDRESS_FIELDS.has(name) ? `address_${name}` : `house_${name}`);
const humanizeErrorKey = (key) => key.replace(/^(house|address)_/, '').replace(/_/g, ' ');

// Client-side required set — mirrors the backend's DataRequired fields.
// Without this, a placeholder save that omits empty fields validates
// against the stored sentinel values and "succeeds" as a no-op.
// `name` is deliberately absent: the property nickname is optional, and a
// blank one is normalized to "My House" by the API.
const REQUIRED_FIELDS = [
    ['street', 'Street address is required'],
    ['city', 'City is required'],
    ['state', 'State is required'],
    ['zip_code', 'Zip code is required'],
];

// Garage width in car widths. Deliberately client-side rather than sourced from
// /houses/config like the other selects: there is no backend enum for this and
// no provider drift to track — it's a fixed list that will never change.
//
// "4+" uses the VALUE '4' with a "4+ car" label. A non-numeric value would fail
// DecimalField coercion in api/forms.py, which fails validate_on_submit() and
// 400s the ENTIRE save, not just this field.
const GARAGE_SIZE_OPTIONS = [
    { value: '1', label: '1 car' },
    { value: '1.5', label: '1.5 car' },
    { value: '2', label: '2 car' },
    { value: '2.5', label: '2.5 car' },
    { value: '3', label: '3 car' },
    { value: '3.5', label: '3.5 car' },
    { value: '4', label: '4+ car' },
];

// Max length of a parking spot label. Mirrors Length(max=32) on
// HouseForm.parking_spot in api/forms.py and max_length=32 on
// HouseFeatures.parking_spot, so an over-long label is stopped at the
// keystroke instead of after a round trip.
const PARKING_SPOT_MAX = 32;

// The parking type "None" — the one value that means "no parking at all".
// Kept as a named constant because three separate places key off it.
const PARKING_TYPE_NONE = 'None';

// Which garage_type values are measured in car widths rather than identified by
// a spot label comes from /houses/config (`sized_garage_types`), NOT from a
// constant here. The backend clears whichever of garage_spaces / parking_spot
// the chosen type disallows, so a local copy that drifted from
// models.enums.SIZED_GARAGE_TYPES would show a field whose value the server
// then silently discards. Server list, one source of truth.
//
// An unselected type keeps the size select — the status quo, and the right
// default for a detached house. Before config resolves the list is empty; only
// an edit-mode load can hit that window, and it settles as soon as config
// lands.
const parkingLayoutFor = (garageType, sizedTypes) => {
    if (garageType === PARKING_TYPE_NONE) return 'none';
    // The config check comes FIRST, ahead of the blank-type default below.
    // Ordering it the other way still rendered the size select in create mode
    // when /houses/config had failed — and with no type options to pair it
    // with, that invites a size saved against a blank type.
    //
    // The split arrives with /houses/config. Until it does — or if the request
    // failed — a type cannot be classified, and guessing is not safe in either
    // direction: guess 'spot' for a garage and the user fills a spot the server
    // then clears; guess 'spaces' for a lot and the size goes the same way.
    // When the card cannot offer a type it should not offer a detail either, so
    // render neither field and say why.
    if (!sizedTypes || sizedTypes.length === 0) return 'unknown';
    // No type chosen yet keeps the size select — the status quo, and the right
    // default for a detached house. Reachable only once config has landed, so
    // a RentCast-autofilled size still has somewhere to show.
    if (!garageType) return 'spaces';
    return sizedTypes.includes(garageType) ? 'spaces' : 'spot';
};

// Square feet per acre. Lot size is stored exclusively in square feet; the
// form's unit toggle converts acre input at typing time.
const SQFT_PER_ACRE = 43560;

const HouseForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;

    usePageView(ANALYTICS_EVENTS.HOUSES_FORM_VIEWED, { is_edit_mode: isEditMode });

    // State
    // Loading starts TRUE in both modes. Edit mode needs to fetchHouse;
    // create mode needs to run the placeholder-redirect check before
    // letting the user submit (otherwise a brief render of the empty
    // create form happens before the async check finishes — and a
    // submission during that window would create a duplicate house).
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    // Tracks failures of the placeholder-check call in create mode. Non-null
    // means the create form is gated behind a retry banner — we cannot safely
    // render the form because the user may have a placeholder primary house
    // we couldn't verify, and submitting would create a duplicate.
    const [placeholderCheckError, setPlaceholderCheckError] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});
    const [addressInput, setAddressInput] = useState('');
    // Address autocomplete is served entirely through the backend proxy
    // (/places/autocomplete + /places/details) so the Google API key never
    // reaches the browser. `suggestions` holds the current predictions and
    // `showSuggestions` controls the dropdown visibility.
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    // Surfaces a manual-entry hint when the backend Places proxy errors. The
    // input stays editable so the user can always fall back to typing the
    // address fields by hand.
    const [placesUnavailable, setPlacesUnavailable] = useState(false);

    // Lot size is STORED exclusively in square feet (formData.lot_size_sqft);
    // these two are presentation-only and never submitted.
    //
    // `lotSizeUnit` drives an input-time converter, not a stored preference:
    // typing in acres multiplies by 43,560 immediately, so the acres → sqft
    // conversion happens exactly once, at the keystroke. Nothing ever divides
    // back on the read path, which is what makes the classic round-trip drift
    // ("typed 0.46 acres, reloads as 0.4599") structurally impossible rather
    // than merely small. Flipping the unit reformats the DISPLAY and leaves
    // lot_size_sqft untouched, so acres → sqft → acres is lossless.
    //
    // Both reset to sqft on load: with no persisted unit preference, the only
    // honest display unit is the one the value is actually stored in.
    const [lotSizeUnit, setLotSizeUnit] = useState('sqft');
    const [lotSizeDisplay, setLotSizeDisplay] = useState('');
    const lotSizeFocusedRef = useRef(false);

    // Config Options State — one key per dropdown the form actually renders.
    // /houses/config also returns roof_types / exterior_types /
    // foundation_types, but those selects were removed (the fields are
    // RentCast-autofill-only now), so we don't hold state we never read.
    // Only the dropdowns still rendered need an option list. heating_types is
    // absent deliberately: the Heating Type <Select> was removed, but the
    // heating_type formData key stays (RentCast still fills it, HouseDetails
    // still shows it) — see the save-contract note on the initializer below.
    const [configOptions, setConfigOptions] = useState({
        property_types: [],
        garage_types: [],
        sized_garage_types: [],
        architecture_types: [],
    });

    // Form Data State
    //
    // The keys of this object are the SAVE CONTRACT, not the render list.
    // Many have no visible input — they still round-trip so RentCast
    // autofill and the POST/PUT path keep working:
    //   - mergeAutofillData only merges a key ALREADY present here
    //     (`key in currentData`), so a missing key silently drops the
    //     autofilled value.
    //   - submit iterates Object.keys(formData), so a missing key is never
    //     appended: a create stores null, and an update silently keeps the
    //     stored value (absent form-data means "preserve"), so the field
    //     goes stale rather than being actively nulled.
    // Deleting a key here silently drops data; deleting an input does not.
    // See CLAUDE.md "formData Keys Are the Save Contract, Not the Render
    // List".
    const [formData, setFormData] = useState({
        // Address
        street: '',
        unit: '',
        city: '',
        state: '',
        zip_code: '',
        country: 'USA',
        county: '',
        // Geocoding + geographic identifiers. No rendered inputs — captured
        // from the Google Places pick (geometry.location) or RentCast autofill,
        // then persisted onto the Address row. Present here because that is
        // what makes both the merge and the submit carry them.
        latitude: '',
        longitude: '',
        county_fips: '',
        state_fips: '',

        // Basic Info
        name: '',
        purchase_date: '',
        purchase_price: '',
        square_footage: '',
        year_built: '',

        // Dimensions
        length: '',
        width: '',
        height: '',

        // Property Details
        property_type: '',
        bedrooms: '',
        bathrooms: '',
        floor_count: '',
        // Booleans default to null (not false) so mergeAutofillData can
        // distinguish "user hasn't touched this" from "user said no". With
        // false initial state, hasAutofillValue(false) === true and the
        // RentCast merge is silently skipped, leaving stored=false even
        // when the provider returned true. Same convention applies to
        // cooling/fireplace/pool: null lets RentCast autofill flow through,
        // and the backend treats absent form-data as "preserve stored value"
        // rather than overwriting with false on every partial PUT.
        has_garage: null,
        cooling: null,
        fireplace: null,
        pool: null,
        // heating_type has no rendered input (the Heating Type select was
        // removed when Systems became the Garage card) but MUST keep its key:
        // RentCast still autofills it and HouseDetails still displays it, so
        // dropping the key would silently stop persisting a value the user can
        // still see. Same reasoning as roof_type / exterior_type below.
        heating_type: '',
        roof_type: '',

        // Detailed Roof
        roof_install_date: '',
        roof_color: '',
        roof_material: '',
        roof_warranty_years: '',
        roof_notes: '',

        // Driveway
        driveway_type: '',
        driveway_install_date: '',
        driveway_length_feet: '',
        driveway_width_feet: '',
        driveway_condition: '',
        driveway_notes: '',

        // Other Details
        garage_type: '',
        garage_spaces: '',
        parking_spot: '',
        architecture_type: '',
        exterior_type: '',
        foundation_type: '',
        room_count: '',
        unit_count: '',

        // Insurance
        home_insurance_company: '',
        home_insurance_policy_number: '',
        home_insurance_expiration: '',
        home_insurance_annual_cost: '',
        home_insurance_deductible: '',
        home_insurance_coverage_amount: '',

        // Warranty
        home_warranty_company: '',
        home_warranty_policy_number: '',
        home_warranty_expiration: '',
        home_warranty_annual_cost: '',
        home_warranty_coverage_details: '',

        // Utilities
        electric_baseline_cost: '',
        gas_baseline_cost: '',
        water_baseline_cost: '',
        internet_baseline_cost: '',
        trash_baseline_cost: '',
        other_utilities_baseline_cost: '',

        // Budgets & Estimates
        annual_maintenance_budget: '',
        annual_improvement_budget: '',
        last_property_tax_amount: '',
        property_tax_year: '',
        current_estimated_value: '',
        estimated_value_date: '',
        estimated_value_source: '',

        // Notes
        warranty_documentation_notes: '',
        insurance_claim_history: '',
        improvement_receipt_notes: '',

        // Provider-agnostic property metadata (auto-fillable from RentCast
        // today). These persist on House top-level columns regardless of
        // whether a third-party provider exists. Not rendered as editable
        // inputs — see the save-contract note on this initializer above.
        // owner_occupied is null (not false) for the same absent-vs-false
        // reason as has_garage above.
        lot_size_sqft: '',
        assessor_id: '',
        subdivision: '',
        zoning: '',
        legal_description: '',
        owner_occupied: null,
        rentcast_property_id: '',
        last_sale_date: '',
        last_sale_price: '',
        last_property_tax_year: '',

        // RentCast "dossier" — structured child records (owner identity, HOA,
        // sale/tax history) that don't map to flat scalar inputs. They must be
        // keys in this initial state so mergeAutofillData (which only merges
        // keys already present) keeps them from the /property-info response
        // instead of dropping them. Not rendered as inputs; bundled into the
        // `property_dossier` JSON field on submit so the backend can upsert
        // them into the property_* child tables.
        //
        // ALL default to null (NOT []): mergeAutofillData only merges when the
        // current value is "empty" per hasAutofillValue, and hasAutofillValue([])
        // is true (a non-empty value), which would BLOCK the incoming arrays
        // from merging. null is the only initial value that lets autofill flow
        // through. The submit guard normalizes null → [] for the histories.
        owner: null,
        hoa: null,
        sale_history: null,
        tax_records: null,
        tax_assessments: null
    });

    // File states
    const [primaryImage, setPrimaryImage] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);

    // References
    // Debounce timer for the backend autocomplete lookup so we don't fire a
    // request on every keystroke.
    const addressDebounceRef = useRef(null);
    // Delayed close of the suggestions dropdown on blur; held in a ref so it
    // can be cleared on unmount.
    const blurTimeoutRef = useRef(null);
    const addressInputRef = useRef(null);

    // Analytics refs — track the funnel without re-rendering.
    // formMountedAtRef powers `duration_ms` on submit/cancel (how long the
    // user spent in the form). isDirtyRef flips true on the first field
    // edit so `houses.form_cancelled` can report abandonment-with-progress.
    // addressWasAutofilledRef records whether the user used Google Places
    // autocomplete vs. typed the address manually. wasPlaceholderRef
    // captures whether an edit-mode house is the auto-seeded onboarding
    // placeholder (false in create mode).
    const formMountedAtRef = useRef(Date.now());
    const isDirtyRef = useRef(false);
    const addressWasAutofilledRef = useRef(false);
    const wasPlaceholderRef = useRef(false);
    // Mirrors previewImage so the unmount-cleanup effect below can revoke the
    // current blob URL without re-running (and revoking a still-in-use URL) on
    // every preview change.
    const previewImageRef = useRef(null);

    // Keep the ref in sync with the latest preview so the unmount effect
    // revokes the right URL. Written via effect (not at each setPreviewImage
    // call site) so no future setter can forget to update it.
    useEffect(() => {
        previewImageRef.current = previewImage;
    }, [previewImage]);

    // Revoke the pending blob preview URL on unmount. handlePrimaryImageFiles /
    // handleRemoveImage already revoke on replace/remove, but SPA exits
    // (Cancel → navigate, successful Submit → navigate) don't reload the
    // document, so without this the object URL would leak for the session.
    useEffect(() => {
        return () => {
            if (previewImageRef.current && previewImageRef.current.startsWith('blob:')) {
                URL.revokeObjectURL(previewImageRef.current);
            }
        };
    }, []);

    // Warn on hard browser navigation (back button / refresh / tab close) when
    // there are unsaved edits. Cancel and a successful Submit navigate via the
    // SPA router, which does NOT trigger beforeunload — so those deliberate
    // exits stay instant, exactly as intended. We can't use React Router's
    // useBlocker here (the app mounts <BrowserRouter>, not a data router), so
    // beforeunload is the portable guard; the browser shows its own generic
    // "Leave site?" dialog (custom copy isn't allowed for beforeunload).
    useEffect(() => {
        const handler = (e) => {
            if (!isDirtyRef.current || submitting) return;
            e.preventDefault();
            e.returnValue = ''; // Chrome requires returnValue to be set.
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [submitting]);

    // Initialization
    useEffect(() => {
        const init = async () => {
            try {
                // Address autocomplete no longer needs a browser-side Maps key
                // — it goes through the backend proxy. Only the house config
                // (dropdown options) needs to be fetched up front.
                let config = {};
                try {
                    config = await HouseService.getConfig();
                } catch (configErr) {
                    console.warn('Failed to fetch house config', configErr);
                    // Without config every dropdown is empty except "— Select —";
                    // tell the user why instead of failing silently.
                    toast.error("Couldn't load property options — dropdowns may be empty. Reload the page to try again.");
                }

                setConfigOptions({
                    property_types: config.property_types || [],
                    heating_types: config.heating_types || [],
                    garage_types: config.garage_types || [],
                    sized_garage_types: config.sized_garage_types || [],
                    architecture_types: config.architecture_types || [],
                });

                if (isEditMode) {
                    await fetchHouse();
                } else {
                    // Create mode: only redirect when the existing primary
                    // is still a placeholder — that means the user is
                    // mid-onboarding and the form should resolve the
                    // placeholder→real transition rather than create a new
                    // house. Real (completed) primaries fall through to a
                    // genuine create flow so users can own multiple
                    // properties.
                    //
                    // 404 is mapped to null by HouseService. Non-404
                    // errors (5xx, network) MUST NOT silently drop into
                    // create mode while we can't verify placeholder
                    // status, or we risk minting a duplicate placeholder.
                    // Gate behind a retry banner instead.
                    try {
                        const primary = await HouseService.getPrimaryHouse();
                        if (primary?.id && primary.is_placeholder) {
                            navigate(`/houses/${primary.id}/edit`, { replace: true });
                            return;
                        }
                    } catch (err) {
                        console.warn('Placeholder check failed; gating create form behind retry banner', err);
                        setPlaceholderCheckError(err?.message || 'Unable to verify your existing properties.');
                        setLoading(false);
                        return;
                    }
                    setLoading(false);
                }
            } catch (err) {
                console.error("Initialization error:", err);
                setError("Failed to initialize form. Please try reloading.");
                toast.error("Failed to initialize form");
                setLoading(false);
            }
        };
        init();
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps -- fetchHouse uses id from closure; adding it without useCallback causes infinite re-renders

    // Clean up any pending debounce timer on unmount so a late autocomplete
    // lookup can't fire after the form is gone.
    useEffect(() => {
        return () => {
            if (addressDebounceRef.current) {
                clearTimeout(addressDebounceRef.current);
            }
            if (blurTimeoutRef.current) {
                clearTimeout(blurTimeoutRef.current);
            }
        };
    }, []);

    // Mirror lot_size_sqft into the display box when it changes from OUTSIDE
    // the input — RentCast autofill and the edit-mode load. Without this the
    // autofilled value persists correctly but never appears on screen.
    //
    // The focus guard is what keeps it from fighting the user: while they're
    // typing, handleLotSizeChange already owns the display string, and letting
    // this effect rewrite it would reformat mid-entry ("2" → "2" is fine, but
    // an acres entry of "0.4" would snap to "17424"). Same guard the shared
    // MoneyInput uses.
    useEffect(() => {
        if (lotSizeFocusedRef.current) return;
        const sqft = parseFloat(formData.lot_size_sqft);
        if (Number.isNaN(sqft)) {
            setLotSizeDisplay('');
            return;
        }
        setLotSizeDisplay(
            lotSizeUnit === 'acres'
                ? String(Math.round((sqft / SQFT_PER_ACRE) * 1000) / 1000)
                : String(Math.round(sqft))
        );
    }, [formData.lot_size_sqft, lotSizeUnit]);

    // Address input handler: update the field and schedule a debounced
    // backend autocomplete lookup. Wiring the debounce here (rather than in a
    // value-watching effect) means programmatic setAddressInput calls — on
    // select and when loading an existing house — don't trigger spurious
    // lookups.
    const handleAddressInputChange = (e) => {
        const value = e.target.value;
        setAddressInput(value);
        isDirtyRef.current = true;

        if (addressDebounceRef.current) {
            clearTimeout(addressDebounceRef.current);
        }

        const query = value.trim();
        if (query.length < 3) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        addressDebounceRef.current = setTimeout(async () => {
            try {
                const result = await HouseService.getPlacesAutocomplete(query);
                if (result?.success && Array.isArray(result?.predictions)) {
                    setPlacesUnavailable(false);
                    setSuggestions(result.predictions);
                    setShowSuggestions(result.predictions.length > 0);
                } else {
                    // Proxy failed (network, quota, key restriction). Surface the
                    // manual-entry hint; the input itself stays usable.
                    setPlacesUnavailable(true);
                    setSuggestions([]);
                    setShowSuggestions(false);
                }
            } catch (err) {
                // A rejected promise (network error) must not leave a stale
                // dropdown or surface an unhandled rejection.
                console.warn('Address autocomplete lookup failed', err);
                setPlacesUnavailable(true);
                setSuggestions([]);
                setShowSuggestions(false);
            }
        }, 300);
    };

    const handleAddressFocus = () => {
        if (suggestions.length > 0) setShowSuggestions(true);
    };

    // Close the dropdown on blur, but on a short delay so a suggestion's
    // onMouseDown fires before the list unmounts. Held in a ref so the timer
    // can be cleared if the component unmounts first.
    const handleAddressBlur = () => {
        blurTimeoutRef.current = setTimeout(() => setShowSuggestions(false), 150);
    };

    // User picked a suggestion: resolve its full address via the details
    // proxy, then parse it into the form fields.
    const handleSuggestionSelect = async (prediction) => {
        setShowSuggestions(false);
        setSuggestions([]);
        try {
            const details = await HouseService.getPlaceDetails(prediction.place_id);
            if (details?.success && details?.result) {
                await handlePlaceSelect(details.result);
            } else {
                setPlacesUnavailable(true);
            }
        } catch (err) {
            console.warn('Address details lookup failed', err);
            setPlacesUnavailable(true);
        }
    };

    const handlePlaceSelect = async (place) => {
        if (!place.address_components) return;

        // Parse Address
        let streetNumber = '';
        let route = '';
        let city = '';
        let state = '';
        let zip = '';
        let country = 'USA';
        let county = '';

        place.address_components.forEach(component => {
            const type = component.types[0];
            if (type === 'street_number') streetNumber = component.long_name;
            if (type === 'route') route = component.long_name;
            if (type === 'locality') city = component.long_name;
            if (type === 'administrative_area_level_1') state = component.short_name;
            if (type === 'postal_code') zip = component.long_name;
            if (type === 'country') country = component.long_name;
            if (type === 'administrative_area_level_2') county = component.long_name;
        });

        const street = `${streetNumber} ${route}`.trim();
        const formattedAddress = place.formatted_address;

        // Deliberately does NOT touch `name`. Selecting an address used to
        // auto-name the house `${street} House`, which made the address the
        // property's identity — the opposite of what a nickname field is for.
        // (It also produced the literal " House" whenever Places returned no
        // street_number/route.) A blank name is normalized to "My House"
        // server-side instead.
        setFormData(prev => ({
            ...prev,
            street,
            city,
            state,
            zip_code: zip,
            country,
            county,
            // Rooftop coordinates from the Places geometry field. Falling back
            // to `prev` (not '') matters: a Details response without geometry
            // must not blank coordinates captured on an earlier pick.
            latitude: place.geometry?.location?.lat ?? prev.latitude,
            longitude: place.geometry?.location?.lng ?? prev.longitude
            // NOTE: `name` is deliberately NOT set here. The nickname is
            // optional and must never be derived from the address; a blank
            // one normalizes to DEFAULT_HOUSE_NAME server-side.
        }));
        setAddressInput(formattedAddress);

        // Mark the address as autocomplete-sourced so submit events can
        // report manual-entry vs. autocomplete usage. Selecting an address
        // is meaningful progress, so it also marks the form dirty —
        // otherwise a select-then-cancel undercounts as was_dirty: false.
        addressWasAutofilledRef.current = true;
        isDirtyRef.current = true;
        analytics.track(ANALYTICS_EVENTS.HOUSES_FORM_ADDRESS_AUTOFILLED, {
            is_edit_mode: isEditMode,
        });

        // Fetch RentCast Data
        await fetchRentCastData(formattedAddress);
    };

    const fetchRentCastData = async (address) => {
        toast.info("Fetching property data...");
        try {
            const result = await HouseService.getPropertyInfo(address);
            if (result.success && result.house) {
                setFormData((prev) => mergeAutofillData(prev, result.house));
                toast.success("Property data Auto-filled!");
            } else {
                // The backend reports every lookup failure (not found, provider
                // outage, timeout) as 200 + {success:false} — without this branch
                // the user never learns auto-fill failed.
                toast.error("Couldn't fetch property data — you can fill in the details manually.");
            }
        } catch (err) {
            console.warn("RentCast error", err);
            toast.error("Couldn't fetch property data — you can fill in the details manually.");
        }
    };

    const fetchHouse = async () => {
        try {
            const data = await HouseService.getHouse(id);
            // If the loaded house is the auto-seeded placeholder, present a
            // clean form rather than showing the literal sentinel values
            // ("TBD" / "00000" / "My Home") to the user. The canonical
            // signal is the House.is_placeholder flag, set by the seeder
            // and flipped to false in update_house when the placeholder
            // is completed.
            const isPlaceholder = data.is_placeholder ?? false;
            // Stash for analytics: distinguishes "completed a placeholder
            // during onboarding" from "edited an existing real property".
            wasPlaceholderRef.current = isPlaceholder;

            // Map API response to form data (same logical mapping as V1)
            setFormData({
                street: isPlaceholder ? '' : (data.address?.street ?? ''),
                unit: data.address?.unit ?? '',
                city: isPlaceholder ? '' : (data.address?.city ?? ''),
                state: isPlaceholder ? '' : (data.address?.state ?? ''),
                zip_code: isPlaceholder ? '' : (data.address?.zip_code ?? ''),
                country: data.address?.country ?? 'USA',
                county: isPlaceholder ? '' : (data.address?.county ?? ''),
                latitude: isPlaceholder ? '' : (data.address?.latitude ?? ''),
                longitude: isPlaceholder ? '' : (data.address?.longitude ?? ''),
                county_fips: isPlaceholder ? '' : (data.address?.county_fips ?? ''),
                state_fips: isPlaceholder ? '' : (data.address?.state_fips ?? ''),

                name: isPlaceholder ? '' : (data.name ?? ''),
                purchase_date: data.purchase_date ?? '',
                purchase_price: data.purchase_price ?? '',
                square_footage: data.square_footage ?? '',
                year_built: data.year_built ?? '',

                length: data.dimensions?.length ?? '',
                width: data.dimensions?.width ?? '',
                height: data.dimensions?.height ?? '',

                property_type: data.property_type ?? '',
                bedrooms: data.bedrooms ?? '',
                bathrooms: data.bathrooms ?? '',
                floor_count: data.floor_count ?? '',
                has_garage: data.has_garage ?? null,
                cooling: data.cooling ?? null,
                fireplace: data.fireplace ?? null,
                pool: data.pool ?? null,
                heating_type: data.heating_type ?? '',
                roof_type: data.roof_type ?? '',

                roof_install_date: data.roof_install_date ?? '',
                roof_color: data.roof_color ?? '',
                roof_material: data.roof_material ?? '',
                roof_warranty_years: data.roof_warranty_years ?? '',
                roof_notes: data.roof_notes ?? '',

                driveway_type: data.driveway_type ?? '',
                driveway_install_date: data.driveway_install_date ?? '',
                driveway_length_feet: data.driveway_length_feet ?? '',
                driveway_width_feet: data.driveway_width_feet ?? '',
                driveway_condition: data.driveway_condition ?? '',
                driveway_notes: data.driveway_notes ?? '',

                garage_type: data.garage_type ?? '',
                garage_spaces: data.garage_spaces ?? '',
                parking_spot: data.parking_spot ?? '',
                architecture_type: data.architecture_type ?? '',
                exterior_type: data.exterior_type ?? '',
                foundation_type: data.foundation_type ?? '',
                room_count: data.room_count ?? '',
                unit_count: data.unit_count ?? '',

                home_insurance_company: data.home_insurance_company ?? '',
                home_insurance_policy_number: data.home_insurance_policy_number ?? '',
                home_insurance_expiration: data.home_insurance_expiration ?? '',
                home_insurance_annual_cost: data.home_insurance_annual_cost ?? '',
                home_insurance_deductible: data.home_insurance_deductible ?? '',
                home_insurance_coverage_amount: data.home_insurance_coverage_amount ?? '',

                home_warranty_company: data.home_warranty_company ?? '',
                home_warranty_policy_number: data.home_warranty_policy_number ?? '',
                home_warranty_expiration: data.home_warranty_expiration ?? '',
                home_warranty_annual_cost: data.home_warranty_annual_cost ?? '',
                home_warranty_coverage_details: data.home_warranty_coverage_details ?? '',

                electric_baseline_cost: data.electric_baseline_cost ?? '',
                gas_baseline_cost: data.gas_baseline_cost ?? '',
                water_baseline_cost: data.water_baseline_cost ?? '',
                internet_baseline_cost: data.internet_baseline_cost ?? '',
                trash_baseline_cost: data.trash_baseline_cost ?? '',
                other_utilities_baseline_cost: data.other_utilities_baseline_cost ?? '',

                annual_maintenance_budget: data.annual_maintenance_budget ?? '',
                annual_improvement_budget: data.annual_improvement_budget ?? '',
                last_property_tax_amount: data.last_property_tax_amount ?? '',
                property_tax_year: data.property_tax_year ?? '',
                current_estimated_value: data.current_estimated_value ?? '',
                estimated_value_date: data.estimated_value_date ?? '',
                estimated_value_source: data.estimated_value_source ?? '',

                warranty_documentation_notes: data.warranty_documentation_notes ?? '',
                insurance_claim_history: data.insurance_claim_history ?? '',
                improvement_receipt_notes: data.improvement_receipt_notes ?? '',

                // Provider-agnostic property metadata. owner_occupied stays
                // null when the column is null in DB so the save path can
                // distinguish "unknown" from "explicitly false" (see the
                // formData initial state comment).
                lot_size_sqft: data.lot_size_sqft ?? '',
                assessor_id: data.assessor_id ?? '',
                subdivision: data.subdivision ?? '',
                zoning: data.zoning ?? '',
                legal_description: data.legal_description ?? '',
                owner_occupied: data.owner_occupied ?? null,
                rentcast_property_id: data.rentcast_property_id ?? '',
                last_sale_date: data.last_sale_date ?? '',
                last_sale_price: data.last_sale_price ?? '',
                last_property_tax_year: data.last_property_tax_year ?? '',

                // Round-trip the persisted dossier so an edit-save re-sends
                // it (the backend upsert is idempotent — same data in, same
                // single row, no duplicates). null (not []) keeps these
                // mergeable if a later autofill runs; submit normalizes null → [].
                owner: data.owner ?? null,
                hoa: data.hoa ?? null,
                sale_history: data.sale_history ?? null,
                tax_records: data.tax_records ?? null,
                tax_assessments: data.tax_assessments ?? null
            });

            if (data.address && !isPlaceholder) {
                setAddressInput(`${data.address.street}, ${data.address.city}, ${data.address.state}`);
            } else {
                // Placeholder houses or houses without an address load with
                // an empty autocomplete display string so stale values from
                // a previous render aren't surfaced. Pairs with the form-
                // value blanking above so the entire form is in a clean
                // "ready to fill" state.
                setAddressInput('');
            }

            // Schema-authoritative URL only — never fabricate from filename.
            // Production has no local-disk storage; the API returns a presigned
            // R2 URL on data.primary_image.url, or null when the image hasn't
            // been migrated. Render a placeholder for null rather than
            // guessing /static/uploads/houses/... which is guaranteed to 404
            // in prod (the canonical Schema-Authoritative URLs pattern from
            // CLAUDE.md, modeled on schemas/item.py::ItemImageSchema.url).
            if (data.primary_image?.url) {
                setPreviewImage(data.primary_image.url);
            }

        } catch (err) {
            setError(err.message || "Failed to load house");
            toast.error("Failed to load house details");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        // First field edit marks the form dirty — powers `was_dirty` on the
        // cancel event so we can measure abandonment-with-progress.
        isDirtyRef.current = true;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        // Errors are stored under the backend's mapped keys
        // (address_street / house_name…), not the bare input name — the old
        // bare-name clear silently never matched.
        const errorKey = fieldErrorKey(name);
        if (validationErrors[errorKey]) {
            setValidationErrors(prev => ({ ...prev, [errorKey]: null }));
        }
    };

    // Parking type needs its own handler rather than handleInputChange: changing
    // the type changes WHICH second field is rendered, and the value that was
    // in the outgoing field would otherwise stay in formData and be submitted
    // under a type it contradicts (a "2 car" size saved against "Street").
    //
    // Clearing here is what the USER sees; it is not what makes the data
    // correct. An emptied key is skipped entirely by handleSubmit, so the
    // backend never hears about it — extract_features_from_form re-applies the
    // "None" rule server-side. This handler and that rule are deliberately
    // belt-and-braces.
    const handleGarageTypeChange = (e) => {
        const nextType = e.target.value;
        const nextLayout = parkingLayoutFor(nextType, configOptions.sized_garage_types);
        isDirtyRef.current = true;
        setFormData((prev) => ({
            ...prev,
            garage_type: nextType,
            garage_spaces: nextLayout === 'spaces' ? prev.garage_spaces : '',
            parking_spot: nextLayout === 'spot' ? prev.parking_spot : '',
        }));
        const errorKey = fieldErrorKey('garage_type');
        if (validationErrors[errorKey]) {
            setValidationErrors((prev) => ({ ...prev, [errorKey]: null }));
        }
    };

    // Lot size needs its own handler rather than handleInputChange: the input
    // renders `lotSizeDisplay` (which may be acres) while formData carries
    // square feet, so the two diverge by design. It must still replicate
    // handleInputChange's dirty-marking and error-clearing, or those regress
    // for this one field.
    const handleLotSizeChange = (e) => {
        const raw = e.target.value;
        isDirtyRef.current = true;
        setLotSizeDisplay(raw);

        const parsed = parseFloat(raw);
        // Round, never truncate: lot_size_sqft is an INTEGER column, and
        // truncating would bias every acre-entered lot small.
        const sqft = Number.isNaN(parsed)
            ? ''
            : Math.round(lotSizeUnit === 'acres' ? parsed * SQFT_PER_ACRE : parsed);
        setFormData(prev => ({ ...prev, lot_size_sqft: sqft }));

        const errorKey = fieldErrorKey('lot_size_sqft');
        if (validationErrors[errorKey]) {
            setValidationErrors(prev => ({ ...prev, [errorKey]: null }));
        }
    };

    // Flipping the unit reformats what's on screen and deliberately leaves
    // formData.lot_size_sqft alone — that's what keeps acres → sqft → acres
    // lossless. Acres display rounds to 3 decimals (~44 sqft of resolution),
    // which is finer than anyone types by hand and, because the stored value
    // is untouched, is non-destructive.
    const handleLotSizeUnitChange = (e) => {
        const nextUnit = e.target.value;
        setLotSizeUnit(nextUnit);

        const sqft = parseFloat(formData.lot_size_sqft);
        if (Number.isNaN(sqft)) {
            return;
        }
        setLotSizeDisplay(
            nextUnit === 'acres'
                ? String(Math.round((sqft / SQFT_PER_ACRE) * 1000) / 1000)
                : String(Math.round(sqft))
        );
    };

    // The primary-image slot goes through useFilePicker (canonical
    // accept="image/*" for the iOS camera sheet, size filtering with toasts,
    // and input-value reset all live in the hook). Files arriving here have
    // already passed the maxSizeMB guard.
    const handlePrimaryImageFiles = (files) => {
        // No image changes while a save is in flight: handleSubmit builds the
        // FormData before awaiting, so a mid-request pick would show a photo
        // state that was never part of the submitted payload.
        if (submitting) return;
        if (!files || files.length === 0) return;
        // Revoke old blob URL if exists
        if (previewImage && previewImage.startsWith('blob:')) {
            URL.revokeObjectURL(previewImage);
        }
        setPrimaryImage(files[0]);
        setPreviewImage(URL.createObjectURL(files[0]));
        // `slot_type` mirrors the old input name. The primary image is the
        // only file slot left on this form. Only fires when a file was
        // actually accepted.
        isDirtyRef.current = true;
        analytics.track(ANALYTICS_EVENTS.HOUSES_FORM_IMAGE_ATTACHED, {
            slot_type: 'image',
            is_edit_mode: isEditMode,
        });
    };

    const { triggerPicker: openPrimaryImagePicker, fileInputElement: primaryImageFileInput } = useFilePicker({
        kind: 'image',
        multiple: false,
        maxSizeMB: MAX_IMAGE_UPLOAD_MB,
        onFiles: handlePrimaryImageFiles,
        disabled: submitting,
    });

    // Clears a freshly-picked (not-yet-saved) property image. The update
    // endpoint has no "delete saved primary image" path, so this only clears a
    // pending blob selection — an already-saved image would just reappear on
    // reload. The remove button is therefore shown only for blob previews.
    const handleRemoveImage = () => {
        if (submitting) return; // same in-flight guard as handlePrimaryImageFiles
        if (previewImage && previewImage.startsWith('blob:')) {
            URL.revokeObjectURL(previewImage);
        }
        setPrimaryImage(null);
        setPreviewImage(null);
        isDirtyRef.current = true;
    };

    // Bring the first offending field into view after a failed save.
    //
    // This replaces a plain window.scrollTo(0, 0), which only worked while the
    // required fields all happened to sit at the top of the document. They no
    // longer do: Property Name lives in the right-hand Property Specs card, and
    // below the `lg` breakpoint the columns stack, so scrolling to the top can
    // land on the image card with the real error offscreen. Keying off the
    // error itself is correct for every required field, not just the first one.
    //
    // `errorKeys` are the prefixed backend keys (house_name / address_street);
    // inputs use bare names, hence the prefix strip. Falls back to the top —
    // where the error banner renders — when nothing matches, which covers the
    // fields that have no rendered input at all (roof_type, floor_count, …).
    const scrollToFirstError = (errorKeys) => {
        for (const key of errorKeys) {
            const name = key.replace(/^(house|address)_/, '');
            const field = document.querySelector(`[name="${name}"]`);
            if (field) {
                // Optional-call both: scrollIntoView is absent in jsdom, and a
                // throw here would abandon the rest of the failure handling
                // (the toast and the error banner) mid-flight.
                field.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
                // focus() after scrollIntoView: it moves the caret for the
                // keyboard user and announces the field to a screen reader,
                // and the smooth scroll still wins visually.
                field.focus?.({ preventScroll: true });
                return;
            }
        }
        window.scrollTo(0, 0);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;

        // Required-field check BEFORE the request. The backend omits empty
        // FormData fields from validation against existing values, so an
        // empty placeholder save used to 200 as a no-op with a false
        // success toast.
        const missing = {};
        REQUIRED_FIELDS.forEach(([field, message]) => {
            if (!String(formData[field] ?? '').trim()) {
                missing[fieldErrorKey(field)] = message;
            }
        });
        if (Object.keys(missing).length > 0) {
            setValidationErrors(missing);
            setError(VALIDATION_MESSAGE);
            toast.error(VALIDATION_MESSAGE);
            analytics.track(ANALYTICS_EVENTS.HOUSES_FORM_SUBMIT_FAILED, {
                is_edit_mode: isEditMode,
                success: false,
                error_code: 'validation_error',
                validation_error_count: Object.keys(missing).length,
            });
            scrollToFirstError(Object.keys(missing));
            return;
        }

        setSubmitting(true);
        setValidationErrors({});
        setError(null);

        // Top of the submit funnel. Pair with houses.form_submit_succeeded /
        // houses.form_submit_failed (both carry the same is_edit_mode) to
        // measure save success rate and time-to-save.
        analytics.track(ANALYTICS_EVENTS.HOUSES_FORM_SUBMIT_ATTEMPTED, {
            is_edit_mode: isEditMode,
            was_placeholder: wasPlaceholderRef.current,
            address_was_autofilled: addressWasAutofilledRef.current,
        });

        const data = new FormData();

        const sanitizeValue = (value) => {
            if (typeof value === 'string') return value.trim();
            return value;
        };

        // Structured RentCast child records are sent as one JSON blob
        // (`property_dossier`) rather than as flat FormData entries —
        // appending an object/array to FormData stringifies it to
        // "[object Object]". Exclude them from the scalar loop below.
        const DOSSIER_KEYS = ['owner', 'hoa', 'sale_history', 'tax_records', 'tax_assessments'];

        // `name` is optional and CLEARABLE, so it must be sent even when
        // empty. update_house builds its WTForms with obj=house, so an
        // omitted key falls back to the STORED value — a user who clears
        // their nickname would silently get the old one back. Sending an
        // explicit "" lets the backend normalize it to "My House".
        const ALWAYS_SEND_KEYS = ['name'];

        Object.keys(formData).forEach(key => {
            if (DOSSIER_KEYS.includes(key)) return;

            // Apply sanitization
            const value = sanitizeValue(formData[key]);

            if (ALWAYS_SEND_KEYS.includes(key)) {
                data.append(key, value ?? '');
                return;
            }

            // Allow 0 and false, but skip null/undefined/empty string
            if (value !== null && value !== undefined && value !== '') {
                data.append(key, value);
            }
        });

        // Bundle the dossier into a single JSON field the backend parses and
        // upserts into the property_* child tables. Only send it when there's
        // real data, so a manual house edit (no RentCast lookup) doesn't ship
        // an empty payload. owner/hoa are objects; the histories are arrays.
        const dossier = {
            owner: formData.owner,
            hoa: formData.hoa,
            sale_history: formData.sale_history || [],
            tax_records: formData.tax_records || [],
            tax_assessments: formData.tax_assessments || []
        };
        const hasDossierData =
            dossier.owner ||
            dossier.hoa ||
            dossier.sale_history.length > 0 ||
            dossier.tax_records.length > 0 ||
            dossier.tax_assessments.length > 0;
        if (hasDossierData) {
            data.append('property_dossier', JSON.stringify(dossier));
        }

        if (primaryImage) data.append('image', primaryImage);

        try {
            const resp = isEditMode
                ? await HouseService.updateHouse(id, data)
                : await HouseService.createHouse(data);
            analytics.track(ANALYTICS_EVENTS.HOUSES_FORM_SUBMIT_SUCCEEDED, {
                is_edit_mode: isEditMode,
                was_placeholder: wasPlaceholderRef.current,
                success: true,
                duration_ms: Date.now() - formMountedAtRef.current,
            });
            // A save can succeed while an attached file fails to upload —
            // the backend reports those as warnings. Surface them so a
            // photo the user attached never silently vanishes. The primary
            // image is the only file this form submits, so there is just
            // one label; the floor_plan / additional_images branches went
            // away with the Documents tab.
            const uploadWarnings = resp?.warnings || [];
            uploadWarnings.forEach((w) => {
                toast.error(`Saved, but the photo upload failed: ${uploadErrorMessage({ error_code: w.error_code, message: w.error }, 'upload failed')}`);
            });
            if (uploadWarnings.length === 0) {
                toast.success("House saved successfully!");
            }
            // Completing the placeholder requires a real address (the
            // backend flips is_placeholder only then). If it's still a
            // placeholder, /houses would just bounce back here — stay put
            // and tell the user what's missing instead.
            if (wasPlaceholderRef.current && resp?.house?.is_placeholder) {
                toast.info('Add the property address to finish setting up your home.');
                setValidationErrors((prev) => ({
                    ...prev,
                    address_street: prev.address_street || 'Required to finish setup',
                }));
                scrollToFirstError(['address_street']);
                return;
            }
            navigate('/houses');
        } catch (err) {
            const isValidationError = err.code === 400 && err.details;
            // An image that slips past the client size guard (or fails another
            // upload check) comes back as a 400 with an error_code but no
            // field `details`. Surface the actionable upload copy instead of
            // the raw server string or the misleading "highlighted fields"
            // banner (which points at fields that are actually fine).
            const isUploadError = !isValidationError && Boolean(err.error_code);
            if (isValidationError) {
                setValidationErrors(err.details);
                // Name the offending fields in the banner. Many backend-
                // validatable fields (roof_type, exterior_type, floor_count,
                // the insurance/utilities keys) have NO rendered input at
                // all — they round-trip through formData only — so a
                // per-field error for them would render nowhere. The banner
                // is the only possible signal for those.
                const offendingKeys = Object.keys(err.details);
                const fieldList = offendingKeys.map(humanizeErrorKey).join(', ');
                setError(`${VALIDATION_MESSAGE}${fieldList ? ` (${fieldList})` : ''}`);
                toast.error(VALIDATION_MESSAGE);
            } else if (isUploadError) {
                const message = uploadErrorMessage(err, err.message);
                setError(message);
                toast.error(message);
            } else {
                setError(err.message || "An error occurred while saving.");
                toast.error(err.message || "Save failed");
            }
            // error_code follows the frontend convention (HOW it failed):
            // a 400 with field details is a validation failure; anything
            // else (5xx, network) is a server error.
            analytics.track(ANALYTICS_EVENTS.HOUSES_FORM_SUBMIT_FAILED, {
                is_edit_mode: isEditMode,
                success: false,
                error_code: isValidationError ? 'validation_error' : 'server_error',
                validation_error_count: isValidationError ? Object.keys(err.details).length : 0,
            });
            // Only a field-level 400 has keys to scroll to; a 5xx / upload
            // failure has none, and its message lives in the top banner.
            if (isValidationError) {
                scrollToFirstError(Object.keys(err.details));
            } else {
                window.scrollTo(0, 0);
            }
        } finally {
            setSubmitting(false);
        }
    };

    // Only the dropdowns still rendered need an option list. roof / exterior
    // / foundation / heating kept their formData keys (RentCast still fills
    // them) but no longer have a <Select>, so they need no options here.
    const propertyTypeOptions = withImportedOption(configOptions.property_types, formData.property_type);
    const garageTypeOptions = withImportedOption(configOptions.garage_types, formData.garage_type);
    const architectureTypeOptions = withImportedOption(configOptions.architecture_types, formData.architecture_type);

    // garage_spaces round-trips as a NUMBER (2, 2.5) from the features JSONB,
    // but GARAGE_SIZE_OPTIONS values are strings. withImportedOption compares
    // with strict ===, so a numeric 2 would match no option and get appended as
    // a duplicate "2" entry. Coerce once here rather than loosening the shared
    // helper, which is correct as-is for its four string-typed callers.
    const garageSpacesValue =
        formData.garage_spaces === '' || formData.garage_spaces === null || formData.garage_spaces === undefined
            ? ''
            : String(formData.garage_spaces);
    const garageSizeOptions = withImportedOption(GARAGE_SIZE_OPTIONS, garageSpacesValue);

    // Which second field the Parking card shows. Derived from the CURRENT
    // type on every render rather than stored in state, so an edit-mode load
    // or a RentCast autofill lands on the right field with no extra syncing.
    const parkingLayout = parkingLayoutFor(formData.garage_type, configOptions.sized_garage_types);

    // Cancel = abandonment. `was_dirty` separates "opened the form and
    // backed out" from "started filling it in and gave up" — the latter
    // is the signal worth investigating.
    const handleCancel = () => {
        analytics.track(ANALYTICS_EVENTS.HOUSES_FORM_CANCELLED, {
            is_edit_mode: isEditMode,
            was_dirty: isDirtyRef.current,
            duration_ms: Date.now() - formMountedAtRef.current,
        });
        navigate('/houses');
    };

    const handleRetryPlaceholderCheck = async () => {
        setPlaceholderCheckError(null);
        setLoading(true);
        try {
            const primary = await HouseService.getPrimaryHouse();
            if (primary?.id && primary.is_placeholder) {
                navigate(`/houses/${primary.id}/edit`, { replace: true });
                return;
            }
        } catch (err) {
            setPlaceholderCheckError(err?.message || 'Unable to verify your existing properties.');
        } finally {
            setLoading(false);
        }
    };

    if (loading) return (
        <div className="flex justify-center items-center h-screen">
            <Loader2 className="animate-spin text-[var(--primary)]" size={48} />
        </div>
    );

    if (placeholderCheckError && !isEditMode) {
        // Gate the create form behind a retry banner. Rendering the form
        // here would let the user submit and create a duplicate house if
        // they actually have a placeholder primary that we couldn't load.
        return (
            <div className="max-w-2xl mx-auto pt-10 px-4">
                <div
                    className="rounded-lg border border-[var(--border)] bg-[var(--bg-card)] p-6 flex items-start space-x-4"
                    data-testid="placeholder-check-error"
                    role="alert"
                >
                    <AlertCircle size={24} className="text-[var(--primary)] flex-shrink-0 mt-1" />
                    <div className="flex-1">
                        <h2 className="font-semibold text-[var(--text-main)] mb-1">
                            Couldn&apos;t verify your existing properties
                        </h2>
                        <p className="text-sm text-[var(--text-secondary)] mb-4">
                            We need to check whether you already have a property in progress before letting you add a new one. {placeholderCheckError}
                        </p>
                        <div className="flex space-x-3">
                            <button
                                type="button"
                                onClick={handleRetryPlaceholderCheck}
                                className="px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-semibold"
                            >
                                Retry
                            </button>
                            <button
                                type="button"
                                onClick={() => navigate('/houses')}
                                className="px-4 py-2 border border-[var(--border)] text-[var(--text-secondary)] rounded-lg hover:bg-[var(--bg-hover)] transition-colors text-sm"
                            >
                                Back to properties
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="animate-fade-in max-w-5xl mx-auto pb-10">
            <PageHeader
                title={isEditMode ? 'Edit Property' : 'Add New Property'}
                subtitle={isEditMode ? 'Update details for your property' : 'Start managing a new property'}
                actions={
                    <>
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="px-4 py-2 border border-[var(--border)] text-[var(--text-secondary)] rounded-lg hover:bg-[var(--bg-hover)] transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={submitting}
                            className="flex items-center space-x-2 px-6 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition-all font-medium disabled:opacity-70"
                        >
                            {submitting && <Loader2 size={16} className="animate-spin" />}
                            <span>{isEditMode ? 'Save Changes' : 'Create Property'}</span>
                        </button>
                    </>
                }
            />

            {error && (
                <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start">
                    <AlertCircle size={20} className="mr-2 mt-0.5 flex-shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column: Image & Location */}
                <div className="space-y-6">
                    {/* Image Card */}
                    <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border)] overflow-hidden shadow-sm">
                        <div className="relative h-56 bg-[var(--bg-hover)] flex items-center justify-center">
                            {previewImage ? (
                                <img src={previewImage} alt="Preview" className="w-full h-full object-cover" />
                            ) : (
                                <div className="text-center text-[var(--text-muted)]">
                                    <Home size={48} className="mx-auto mb-2 opacity-30" />
                                    <p className="text-sm">No image selected</p>
                                </div>
                            )}
                            <div className="absolute bottom-3 right-3 flex gap-2">
                                {/* Remove is only offered for a freshly-picked (blob)
                                    image — the update endpoint can't delete an
                                    already-saved primary image, so clearing one
                                    locally would just reappear on reload. */}
                                {previewImage && previewImage.startsWith('blob:') && (
                                    <button
                                        type="button"
                                        onClick={handleRemoveImage}
                                        disabled={submitting}
                                        aria-label="Remove image"
                                        title="Remove image"
                                        className="bg-[var(--bg-card)] hover:bg-[var(--bg-hover)] text-[var(--text-main)] p-2 rounded-lg shadow-md cursor-pointer border border-[var(--border)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <X size={18} />
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={openPrimaryImagePicker}
                                    disabled={submitting}
                                    aria-label="Upload image"
                                    title="Upload image"
                                    className="bg-[var(--bg-card)] hover:bg-[var(--bg-hover)] text-[var(--text-main)] p-2 rounded-lg shadow-md cursor-pointer border border-[var(--border)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Upload size={18} />
                                </button>
                                {primaryImageFileInput}
                            </div>
                        </div>
                        <div className="p-4 border-t border-[var(--border)]">
                            <h3 className="font-semibold text-[var(--text-main)] mb-1">Property Image</h3>
                            <p className="text-xs text-[var(--text-secondary)]">
                                Upload a photo or we&apos;ll look for one on Street View.
                            </p>
                        </div>
                    </div>

                    {/* Address Card */}
                    <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border)] p-5 shadow-sm">
                        <h3 className="font-bold text-[var(--text-main)] mb-4 flex items-center">
                            <MapPin size={18} className="mr-2 text-[var(--primary)]" />
                            Location
                        </h3>

                        <div className="mb-4">
                            <label className="block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-1">
                                Address Search <span className="text-[var(--primary)] text-[10px] ml-1 bg-[var(--primary-bg)] px-1.5 py-0.5 rounded">AUTO-FILL</span>
                            </label>
                            <div className="relative">
                                <Search className="absolute left-3 top-2.5 text-[var(--text-muted)]" size={18} />
                                <input
                                    ref={addressInputRef}
                                    type="text"
                                    placeholder="Start typing address..."
                                    value={addressInput}
                                    onChange={handleAddressInputChange}
                                    onFocus={handleAddressFocus}
                                    onBlur={handleAddressBlur}
                                    autoComplete="off"
                                    className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)] focus:outline-none"
                                    data-testid="house-address-search"
                                />
                                {showSuggestions && suggestions.length > 0 && (
                                    <ul
                                        className="absolute z-20 left-0 right-0 mt-1 max-h-60 overflow-auto rounded-lg border border-[var(--border)] bg-[var(--bg-card)] shadow-lg"
                                        data-testid="address-suggestions"
                                    >
                                        {suggestions.map((prediction) => (
                                            <li key={prediction.place_id}>
                                                {/* onMouseDown (not onClick) so the selection fires
                                                    before the input's onBlur closes the dropdown. */}
                                                <button
                                                    type="button"
                                                    onMouseDown={() => handleSuggestionSelect(prediction)}
                                                    className="w-full text-left px-3 py-2 text-sm text-[var(--text-main)] hover:bg-[var(--bg-hover)] focus:bg-[var(--bg-hover)] focus:outline-none"
                                                >
                                                    {prediction.description}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            {placesUnavailable && (
                                <p className="text-xs text-[var(--text-muted)] mt-1.5" data-testid="maps-load-failed-hint">
                                    Address autocomplete is unavailable right now. Please fill in the street, city, state, and ZIP fields below directly.
                                </p>
                            )}
                        </div>

                        <div className="space-y-4">
                            <InputGroup label="Street Address" error={validationErrors.address_street} required>
                                <Input name="street" value={formData.street} onChange={handleInputChange} />
                            </InputGroup>
                            <div className="grid grid-cols-2 gap-4">
                                <InputGroup label="City" error={validationErrors.address_city} required>
                                    <Input name="city" value={formData.city} onChange={handleInputChange} />
                                </InputGroup>
                                <InputGroup label="State" error={validationErrors.address_state} required>
                                    <Input name="state" value={formData.state} onChange={handleInputChange} />
                                </InputGroup>
                            </div>
                            <InputGroup label="County" error={validationErrors.address_county}>
                                <Input name="county" value={formData.county} onChange={handleInputChange} />
                            </InputGroup>
                            <div className="grid grid-cols-2 gap-4">
                                <InputGroup label="Zip Code" error={validationErrors.address_zip_code} required>
                                    <Input name="zip_code" value={formData.zip_code} onChange={handleInputChange} />
                                </InputGroup>
                                <InputGroup label="Unit/Apt" error={validationErrors[fieldErrorKey('unit')]}>
                                    <Input name="unit" value={formData.unit} onChange={handleInputChange} />
                                </InputGroup>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column: editorial chapters, all always visible.

                    The legacy V2Tabs strip (Systems / Financials /
                    Documents) and the Architecture/Roof/Layout sections
                    were removed: they duplicated surfaces that now live on
                    dedicated tables and HouseDetails tabs (Utility /
                    UtilityBill, HouseInsurancePolicy, HouseFloorPlan), and
                    the fields they wrote were never read back. Systems
                    survives as a plain chapter below. The removed fields
                    keep their formData keys so RentCast autofill still
                    persists them — see the save-contract note on the
                    formData initializer.

                    The right column is a single full-width content track —
                    no sidebar rail. (A previous attempt used a 180px
                    sidebar rail which left the chapter column too narrow
                    for its 3-up Property Specs grid.) */}
                <div
                    className="lg:col-span-2 space-y-8"
                    data-testid="house-form-document"
                    role="group"
                    aria-label={isEditMode ? 'Edit property details' : 'New property details'}
                >
                    <section
                        data-chapter="identity"
                        data-testid="chapter-identity"
                        aria-label="Property identity"
                        className="bg-[var(--bg-card)] rounded-xl border border-[var(--border)] p-6 shadow-sm scroll-mt-24"
                    >
                        <div className="space-y-6">
                            <FormSection title="Property Specs">
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                    {/* Property Name leads the specs: it is what
                                        every other surface labels the house by,
                                        so it reads first in the identity
                                        chapter. Optional — a blank nickname
                                        normalizes to DEFAULT_HOUSE_NAME
                                        server-side and is never derived from
                                        the address. Spans the full row: it is
                                        free text among numeric neighbours and
                                        reads cramped in a one-third cell.

                                        An explicit `id` is required here:
                                        InputGroup only clones a *single* child
                                        to inject one, and this group has two
                                        (the input plus the hint), so without it
                                        the <label htmlFor> would point at
                                        nothing.

                                        maxLength mirrors the server cap
                                        (House.name is String(128)) so an
                                        over-long nickname is stopped at the
                                        keystroke instead of after a round
                                        trip. */}
                                    <div className="col-span-2 md:col-span-3">
                                        <InputGroup
                                            id="house-name"
                                            label="Property Name / Nickname (optional)"
                                            error={validationErrors.house_name}
                                        >
                                            <Input
                                                id="house-name"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                placeholder='e.g. "The Lake House"'
                                                maxLength={128}
                                            />
                                            <p className="mt-1 text-xs text-[var(--text-muted)]">
                                                Give it a nickname you&apos;ll smile at — or leave it blank and we&apos;ll call it My House.
                                            </p>
                                        </InputGroup>
                                    </div>
                                    <InputGroup label="Property Type" error={validationErrors[fieldErrorKey('property_type')]}>
                                        <Select name="property_type" value={formData.property_type} onChange={handleInputChange} placeholder="— Select —">
                                            {propertyTypeOptions.map((option) => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </Select>
                                    </InputGroup>
                                    <InputGroup label="Architecture Style" error={validationErrors[fieldErrorKey('architecture_type')]}>
                                        <Select name="architecture_type" value={formData.architecture_type || ''} onChange={handleInputChange} placeholder="— Select —">
                                            {architectureTypeOptions.map((option) => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </Select>
                                    </InputGroup>
                                    <InputGroup label="Year Built" error={validationErrors[fieldErrorKey('year_built')]}>
                                        <Input type="number" name="year_built" value={formData.year_built} onChange={handleInputChange} />
                                    </InputGroup>
                                    <InputGroup label="Square Footage" error={validationErrors[fieldErrorKey('square_footage')]}>
                                        <Input type="number" name="square_footage" value={formData.square_footage} onChange={handleInputChange} />
                                    </InputGroup>
                                    {/* Lot size is always STORED in square feet;
                                        the unit select is an input-time
                                        converter. It carries no `name` on
                                        purpose — a name matching a formData key
                                        would make it a phantom submitted field. */}
                                    <InputGroup label="Lot Size" error={validationErrors[fieldErrorKey('lot_size_sqft')]}>
                                        <div className="flex gap-2">
                                            <Input
                                                type="number"
                                                step={lotSizeUnit === 'acres' ? '0.001' : '1'}
                                                name="lot_size_sqft"
                                                value={lotSizeDisplay}
                                                onChange={handleLotSizeChange}
                                                onFocus={() => { lotSizeFocusedRef.current = true; }}
                                                onBlur={() => { lotSizeFocusedRef.current = false; }}
                                                className="flex-1 min-w-0"
                                            />
                                            <select
                                                aria-label="Lot size unit"
                                                data-testid="lot-size-unit"
                                                value={lotSizeUnit}
                                                onChange={handleLotSizeUnitChange}
                                                className="shrink-0 rounded-lg border border-[var(--border)] bg-[var(--bg-hover)] text-[var(--text-main)] px-2 py-2.5 text-sm focus:ring-2 focus:ring-[var(--primary)] focus:outline-none"
                                            >
                                                <option value="sqft">sq ft</option>
                                                <option value="acres">acres</option>
                                            </select>
                                        </div>
                                    </InputGroup>
                                    <InputGroup label="Bedrooms" error={validationErrors[fieldErrorKey('bedrooms')]}>
                                        <Input type="number" name="bedrooms" value={formData.bedrooms} onChange={handleInputChange} />
                                    </InputGroup>
                                    <InputGroup label="Bathrooms" error={validationErrors[fieldErrorKey('bathrooms')]}>
                                        <Input type="number" step="0.5" name="bathrooms" value={formData.bathrooms} onChange={handleInputChange} />
                                    </InputGroup>
                                </div>
                            </FormSection>

                            <FormSection title="Purchase">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <InputGroup label="Purchase Date" error={validationErrors[fieldErrorKey('purchase_date')]}>
                                        <DateField name="purchase_date" value={formData.purchase_date} onChange={handleInputChange} />
                                    </InputGroup>
                                    <InputGroup label="Purchase Price" error={validationErrors[fieldErrorKey('purchase_price')]}>
                                        <div className="relative">
                                            <span className="absolute left-3 top-2.5 text-[var(--text-muted)]">$</span>
                                            <Input className="pl-7" type="number" name="purchase_price" value={formData.purchase_price} onChange={handleInputChange} />
                                        </div>
                                    </InputGroup>
                                </div>
                            </FormSection>
                        </div>
                    </section>

                    <section
                        data-chapter="parking"
                        data-testid="chapter-parking"
                        aria-label="Parking"
                        className="bg-[var(--bg-card)] rounded-xl border border-[var(--border)] p-6 shadow-sm scroll-mt-24"
                    >
                        <div className="space-y-6">
                            <FormSection title="Parking">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <InputGroup label="Parking Type" error={validationErrors[fieldErrorKey('garage_type')]}>
                                        <Select name="garage_type" value={formData.garage_type} onChange={handleGarageTypeChange} placeholder="— Select —">
                                            {garageTypeOptions.map((option) => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </Select>
                                    </InputGroup>
                                    {/* The second field follows the type: a car-width size for a
                                        garage, a spot label for an assigned space, and nothing at
                                        all for "None" — where any second value would contradict
                                        the answer just given. */}
                                    {parkingLayout === 'spaces' && (
                                        <InputGroup label="Garage Size" error={validationErrors[fieldErrorKey('garage_spaces')]}>
                                            <Select name="garage_spaces" value={garageSpacesValue} onChange={handleInputChange} placeholder="— Select —">
                                                {garageSizeOptions.map((option) => (
                                                    <option key={option.value} value={option.value}>{option.label}</option>
                                                ))}
                                            </Select>
                                        </InputGroup>
                                    )}
                                    {parkingLayout === 'unknown' && (
                                        <p className="self-center text-xs text-[var(--text-muted)]" data-testid="parking-detail-unavailable">
                                            Parking options couldn&apos;t be loaded, so the size and spot fields are
                                            hidden to avoid saving one against the wrong parking type. Reload to try again.
                                        </p>
                                    )}
                                    {parkingLayout === 'spot' && (
                                        <InputGroup label="Spot / Space #" error={validationErrors[fieldErrorKey('parking_spot')]}>
                                            <Input
                                                name="parking_spot"
                                                value={formData.parking_spot}
                                                onChange={handleInputChange}
                                                placeholder='e.g. "B-7"'
                                                maxLength={PARKING_SPOT_MAX}
                                            />
                                        </InputGroup>
                                    )}
                                </div>
                            </FormSection>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default HouseForm;
